import React, { useState, useMemo } from "react";
import { FiCalendar, FiMapPin, FiCheckCircle, FiX, FiClock, FiMonitor, FiAlertCircle, FiChevronLeft, FiChevronRight, FiTrendingUp, FiActivity, FiFilter, FiArrowDown, FiArrowUp } from "react-icons/fi";
import { IconWrapper } from "../../IconWrapper";
import { Booking } from "./types";
import { toLocalDateString } from "../../../utils/dateUtils";
import { Tooltip, useColorMode } from "@chakra-ui/react";

interface MyBookingsListProps {
  bookings: Booking[];
  onCancelBooking: (bookingId: string) => void;
  theme: any;
}

export const MyBookingsList: React.FC<MyBookingsListProps> = ({ 
  bookings, 
  onCancelBooking,
  theme 
}) => {
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending' | 'cancelled'>('all');
  const [dateSort, setDateSort] = useState<'newest' | 'oldest'>('newest');

  // Get unique booked dates
  const bookedDates = useMemo(() => {
    return new Set(bookings.map(b => b.date));
  }, [bookings]);

  // Calculate analytics
  const analytics = useMemo(() => {
    const total = bookings.length;
    const pending = bookings.filter(b => b.status === 'pending').length;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const upcoming = bookings.filter(b => {
      const bookingDate = new Date(b.date);
      bookingDate.setHours(0, 0, 0, 0);
      return bookingDate >= today && (b.status === 'active' || b.status === 'pending');
    }).length;

    // Most booked floor
    const floorCounts: { [key: string]: number } = {};
    bookings.forEach(b => {
      floorCounts[b.floorName] = (floorCounts[b.floorName] || 0) + 1;
    });
    const mostBooked = Object.entries(floorCounts).sort((a, b) => b[1] - a[1])[0];

    return {
      total,
      pending,
      upcoming,
      mostBookedFloor: mostBooked ? mostBooked[0] : 'N/A',
      mostBookedCount: mostBooked ? mostBooked[1] : 0
    };
  }, [bookings]);

  // Filter and Sort bookings
  const sortedBookings = useMemo(() => {
    let result = [...bookings];

    // 1. Date Filter (from Calendar)
    if (selectedCalendarDate) {
      result = result.filter(b => b.date === selectedCalendarDate);
    }

    // 2. Status Filter
    if (statusFilter !== 'all') {
      result = result.filter(b => b.status === statusFilter);
    }

    // 3. Date Sort
    result.sort((a, b) => {
      const timeA = new Date(a.date).getTime();
      const timeB = new Date(b.date).getTime();
      return dateSort === 'newest' ? timeB - timeA : timeA - timeB;
    });

    return result;
  }, [bookings, selectedCalendarDate, statusFilter, dateSort]);

  // Calendar generation
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days: (Date | null)[] = [];
    
    // Add empty slots for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days in month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  }, [currentMonth]);

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handlePrevYear = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear() - 1, currentMonth.getMonth()));
  };

  const handleNextYear = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear() + 1, currentMonth.getMonth()));
  };

  const handleDateClick = (date: Date) => {
    const dateStr = toLocalDateString(date);
    // Toggle: if same date clicked, show all bookings
    setSelectedCalendarDate(selectedCalendarDate === dateStr ? null : dateStr);
  };

  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  if (bookings.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 rounded-2xl border-2 border-dashed border-gray-100 dark:border-gray-800 opacity-60">
        <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-full mb-4">
           <IconWrapper icon={FiCalendar} size={32} className="text-gray-400" />
        </div>
        <p className="text-sm font-bold uppercase tracking-widest text-gray-500">No Reservations</p>
        <p className="text-xs text-gray-400 mt-2 text-center max-w-[200px]">
          Your upcoming seat bookings will be listed here.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Section: Calendar (50%) + Analytics (50%) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar - Premium Design */}
        <div className={`relative overflow-hidden rounded-md border shadow-sm ${
          isDark 
            ? 'bg-[#1A1C1F] border-[#2E2F33]' 
            : 'bg-gradient-to-br from-white to-gray-50 border-gray-200'
        }`}>
          <style>
            {`
              .gray-scrollbar::-webkit-scrollbar {
                width: 6px;
                height: 6px;
              }
              .gray-scrollbar::-webkit-scrollbar-track {
                background: transparent;
              }
              .gray-scrollbar::-webkit-scrollbar-thumb {
                background-color: ${isDark ? '#4B5563' : '#E5E7EB'}; 
                border-radius: 20px;
              }
              .gray-scrollbar::-webkit-scrollbar-thumb:hover {
                 background-color: ${isDark ? '#6B7280' : '#D1D5DB'}; 
              }
            `}
          </style>
          {/* Decorative elements */}
          {isDark && (
            <>
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#B59143]/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl" />
            </>
          )}
          
            <div className="relative z-10 p-3">
            <div className="flex items-center justify-between mb-5">
              {/* Month Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevMonth}
                  className={`p-1.5 rounded-lg transition-all ${
                    isDark 
                      ? 'hover:bg-[#2A3441] text-gray-400 hover:text-[#B59143]' 
                      : 'hover:bg-gray-100 text-gray-600 hover:text-[#B59143]'
                  }`}
                >
                  <IconWrapper icon={FiChevronLeft} size={14} />
                </button>
                <h3 className={`text-base font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'} w-24 text-center`}>
                  {currentMonth.toLocaleDateString('en-US', { month: 'long' })}
                </h3>
                <button
                  onClick={handleNextMonth}
                  className={`p-1.5 rounded-lg transition-all ${
                    isDark 
                      ? 'hover:bg-[#2A3441] text-gray-400 hover:text-[#B59143]' 
                      : 'hover:bg-gray-100 text-gray-600 hover:text-[#B59143]'
                  }`}
                >
                  <IconWrapper icon={FiChevronRight} size={14} />
                </button>
              </div>

              {/* Year Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePrevYear}
                  className={`p-1.5 rounded-lg transition-all ${
                    isDark 
                      ? 'hover:bg-[#2A3441] text-gray-400 hover:text-[#B59143]' 
                      : 'hover:bg-gray-100 text-gray-600 hover:text-[#B59143]'
                  }`}
                >
                  <IconWrapper icon={FiChevronLeft} size={14} />
                </button>
                <h3 className={`text-base font-black tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {currentMonth.getFullYear()}
                </h3>
                <button
                  onClick={handleNextYear}
                  className={`p-1.5 rounded-lg transition-all ${
                    isDark 
                      ? 'hover:bg-[#2A3441] text-gray-400 hover:text-[#B59143]' 
                      : 'hover:bg-gray-100 text-gray-600 hover:text-[#B59143]'
                  }`}
                >
                  <IconWrapper icon={FiChevronRight} size={14} />
                </button>
              </div>
            </div>

            {/* Calendar Grid - Smaller */}
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, idx) => (
                <div key={`${day}-${idx}`} className={`text-center text-[12px] font-bold uppercase tracking-wider pb-1 ${isDark ? 'text-gray-400' : 'text-gray-400'}`}>
                  {day}
                </div>
              ))}
              
              {/* Calendar days */}
              {calendarDays.map((day, idx) => {
                if (!day) {
                  return <div key={`empty-${idx}`} className="w-12 h-12" />;
                }
                
                const dateStr = toLocalDateString(day);
                const hasBooking = bookedDates.has(dateStr);
                const isSelected = selectedCalendarDate === dateStr;
                // Fix today date comparison
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const checkDay = new Date(day);
                checkDay.setHours(0, 0, 0, 0);
                const isToday = checkDay.getTime() === today.getTime();
                
                return (
                  <button
                    key={dateStr}
                    onClick={() => hasBooking && handleDateClick(day)}
                    disabled={!hasBooking}
                    className={`
                      w-12 h-12 rounded-md text-xs font-bold transition-all relative
                      ${hasBooking ? 'cursor-pointer' : 'cursor-default'}
                      ${isSelected 
                        ? 'bg-gradient-to-br from-[#FBD282] to-[#B59143] text-white shadow-sm shadow-[#B59143]/30 scale-105' 
                        : hasBooking
                          ? isDark 
                            ? 'bg-[#B59143]/10 text-[#FBD282] hover:bg-[#B59143]/20 border border-[#B59143]/20 hover:border-[#B59143]/40'
                            : 'bg-[#FFF8E6] text-[#B59143] hover:bg-[#FBD282]/40 border border-[#B59143]/30 hover:border-[#B59143]/50'
                          : isDark
                            ? 'text-gray-200 hover:bg-[#2A3441]/30 border border-transparent'
                            : 'text-gray-500 hover:bg-gray-100 border border-transparent'
                      }
                      ${isToday && !isSelected ? (isDark ? 'ring-2 ring-blue-400' : 'ring-2 ring-blue-500') : ''}
                    `}
                  >
                    {day.getDate()}
                    {hasBooking && !isSelected && (
                      <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-[#B59143]" />
                    )}
                  </button>
                );
              })}
            </div>

            {selectedCalendarDate && (
              <div className={`mt-4 pt-3 border-t ${isDark ? 'border-[#2E2F33]' : 'border-gray-200'}`}>
                <button
                  onClick={() => setSelectedCalendarDate(null)}
                  className={`text-xs font-semibold flex items-center gap-1 transition-colors ${
                    isDark ? 'text-[#B59143] hover:text-[#FBD282]' : 'text-[#B59143] hover:text-[#8B6F33]'
                  }`}
                >
                  <IconWrapper icon={FiChevronLeft} size={10} />
                  Show all bookings
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Analytics - Premium Cards in 2x2 Grid */}
        <div className="grid grid-cols-2 gap-3">
          {/* Total Bookings */}
          <div className={`relative overflow-hidden rounded-xl border shadow-md group ${
            isDark 
              ? 'bg-[#1A1C1F] border-[#2E2F33]' 
              : 'bg-gradient-to-br from-white via-blue-50/20 to-white border-blue-100'
          }`}>
            {isDark && <div className="absolute top-0 right-0 w-20 h-20 bg-blue-500/5 rounded-full blur-2xl" />}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 via-blue-500/0 to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative z-10 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-full transition-transform group-hover:scale-110 duration-300 ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
                  <IconWrapper icon={FiCalendar} size={18} className="text-blue-500" />
                </div>
                <div className="text-right">
                  <div className={`text-[9px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total</div>
                  <div className={`text-3xl font-black ${isDark ? 'text-gray-200' : 'text-gray-900'} leading-none mt-1`}>{analytics.total}</div>
                </div>
              </div>
              <div className={`text-[14px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>All bookings</div>
              <div className={`mt-2 pt-2 border-t ${isDark ? 'border-[#2E2F33]' : 'border-blue-100'}`}>
                <div className="flex items-center gap-1">
                  <div className={`text-[12px] font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                    {analytics.total > 0 ? '100%' : '0%'}
                  </div>
                  <div className={`text-[12px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>of all reservations</div>
                </div>
              </div>
            </div>
          </div>

          {/* Pending */}
          <div className={`relative overflow-hidden rounded-xl border shadow-md group ${
            isDark 
              ? 'bg-[#1A1C1F] border-[#2E2F33]' 
              : 'bg-gradient-to-br from-white via-orange-50/20 to-white border-orange-100'
          }`}>
            {isDark && <div className="absolute top-0 right-0 w-20 h-20 bg-orange-500/5 rounded-full blur-2xl" />}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500/0 via-orange-500/0 to-orange-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative z-10 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-full transition-transform group-hover:scale-110 duration-300 ${isDark ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                  <IconWrapper icon={FiClock} size={18} className="text-orange-500" />
                </div>
                <div className="text-right">
                  <div className={`text-[9px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Pending</div>
                  <div className={`text-3xl font-black ${isDark ? 'text-white' : 'text-gray-900'} leading-none mt-1`}>{analytics.pending}</div>
                </div>
              </div>
              <div className={`text-[14px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Awaiting approval</div>
              <div className={`mt-2 pt-2 border-t ${isDark ? 'border-[#2E2F33]' : 'border-orange-100'}`}>
                <div className="flex items-center gap-1">
                  <div className={`text-[12px] font-semibold ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>
                    {analytics.total > 0 ? Math.round((analytics.pending / analytics.total) * 100) : 0}%
                  </div>
                  <div className={`text-[12px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>needs review</div>
                </div>
              </div>
            </div>
          </div>

          {/* Upcoming */}
          <div className={`relative overflow-hidden rounded-xl border shadow-md group ${
            isDark 
              ? 'bg-[#1A1C1F] border-[#2E2F33]' 
              : 'bg-gradient-to-br from-white via-green-50/20 to-white border-green-100'
          }`}>
            {isDark && <div className="absolute top-0 right-0 w-20 h-20 bg-green-500/5 rounded-full blur-2xl" />}
            <div className="absolute inset-0 bg-gradient-to-br from-green-500/0 via-green-500/0 to-green-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative z-10 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-full transition-transform group-hover:scale-110 duration-300 ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                  <IconWrapper icon={FiTrendingUp} size={18} className="text-green-500" />
                </div>
                <div className="text-right">
                  <div className={`text-[9px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Upcoming</div>
                  <div className={`text-3xl font-black ${isDark ? 'text-white' : 'text-gray-900'} leading-none mt-1`}>{analytics.upcoming}</div>
                </div>
              </div>
              <div className={`text-[14px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Future bookings</div>
              <div className={`mt-2 pt-2 border-t ${isDark ? 'border-[#2E2F33]' : 'border-green-100'}`}>
                <div className="flex items-center gap-1">
                  <IconWrapper icon={FiTrendingUp} size={10} className={isDark ? 'text-green-400' : 'text-green-600'} />
                  <div className={`text-[12px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>scheduled ahead</div>
                </div>
              </div>
            </div>
          </div>

          {/* Most Booked Floor */}
          <div className={`relative overflow-hidden rounded-xl border shadow-md group ${
            isDark 
              ? 'bg-[#1A1C1F] border-[#2E2F33]' 
              : 'bg-gradient-to-br from-white via-amber-50/20 to-white border-amber-100'
          }`}>
            {isDark && <div className="absolute top-0 right-0 w-20 h-20 bg-[#B59143]/5 rounded-full blur-2xl" />}
            <div className="absolute inset-0 bg-gradient-to-br from-[#B59143]/0 via-[#B59143]/0 to-[#B59143]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative z-10 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-full transition-transform group-hover:scale-110 duration-300 ${isDark ? 'bg-[#B59143]/20' : 'bg-[#FFF8E6]'}`}>
                  <IconWrapper icon={FiActivity} size={18} className="text-[#B59143]" />
                </div>
                <div className="text-right">
                  <div className={`text-[9px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Top Floor</div>
                  <div className={`text-3xl font-black ${isDark ? 'text-white' : 'text-gray-900'} leading-none mt-1 truncate max-w-[80px]`}>{analytics.mostBookedFloor}</div>
                </div>
              </div>
              <div className={`text-[14px] font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{analytics.mostBookedCount} bookings</div>
              <div className={`mt-2 pt-2 border-t ${isDark ? 'border-[#2E2F33]' : 'border-amber-100'}`}>
                <div className="flex items-center gap-1">
                  <div className={`text-[12px] font-semibold ${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'}`}>
                    Most popular
                  </div>
                  <IconWrapper icon={FiActivity} size={10} className={isDark ? 'text-[#FBD282]' : 'text-[#B59143]'} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section: Premium Table */}
      <div>
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {selectedCalendarDate 
                ? (() => {
                    const [year, month, day] = selectedCalendarDate.split('-').map(Number);
                    // Add 1 day to correct for ISO shift
                    const localDate = new Date(year, month - 1, day);
                    localDate.setDate(localDate.getDate() + 1);
                    return `Bookings for ${localDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
                  })()
                : 'All Bookings'
              }
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'} mt-1`}>
              {sortedBookings.length} {sortedBookings.length === 1 ? 'booking' : 'bookings'} found
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className={`appearance-none pl-9 pr-8 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border outline-none cursor-pointer transition-all ${
                  isDark 
                    ? 'bg-[#1A1C1F] border-[#2E2F33] text-gray-300 hover:border-[#B59143]/50 focus:border-[#B59143]' 
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 focus:border-blue-500'
                }`}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
          
              </select>
              <IconWrapper 
                icon={FiFilter} 
                size={12} 
                className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} 
              />
            </div>

            {/* Date Sort */}
            <div className="relative">
              <button
                onClick={() => setDateSort(prev => prev === 'newest' ? 'oldest' : 'newest')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border transition-all ${
                  isDark 
                    ? 'bg-[#1A1C1F] border-[#2E2F33] text-gray-300 hover:border-[#B59143]/50' 
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <span>{dateSort === 'newest' ? 'Newest' : 'Oldest'}</span>
                <IconWrapper icon={dateSort === 'newest' ? FiArrowDown : FiArrowUp} size={12} />
              </button>
            </div>
          </div>
        </div>

        {sortedBookings.length === 0 ? (
          <div className={`flex flex-col items-center justify-center py-12 px-4 rounded-2xl border-2 border-dashed ${isDark ? 'border-gray-800' : 'border-gray-200'} opacity-60`}>
            <div className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'} p-4 rounded-full mb-4`}>
              <IconWrapper icon={FiAlertCircle} size={32} className="text-gray-400" />
            </div>
            <p className="text-sm font-bold uppercase tracking-widest text-gray-500">No Bookings</p>
            <p className="text-xs text-gray-400 mt-2 text-center max-w-[250px]">
              {selectedCalendarDate 
                ? 'No bookings found for this date. Try selecting a different date from the calendar.'
                : 'You have no bookings yet. Book a seat to get started.'
              }
            </p>
          </div>
        ) : (
          <div className={`relative overflow-hidden rounded-md border shadow-sm ${
            isDark 
              ? 'bg-[#1A1C1F] border-[#2E2F33]' 
              : 'bg-white border-gray-200'
          }`}>
            {/* Decorative elements */}
            {isDark && (
              <div className="absolute top-0 right-0 w-48 h-48 bg-[#B59143]/3 rounded-full blur-3xl" />
            )}
            
            {/* Table Header */}
            <div className={`relative z-10 grid grid-cols-[100px_1fr_180px_140px_110px_90px] gap-4 px-6 py-3 border-b ${
              isDark 
                ? 'bg-[#2A3441]/50 backdrop-blur-sm border-[#2E2F33]' 
                : 'bg-gray-50 border-gray-200'
            }`}>
              <div className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Date</div>
              <div className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Seat</div>
              <div className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Floor</div>
              <div className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Time</div>
              <div className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Status</div>
              <div className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>Action</div>
            </div>


            {/* Scrollable Table Body */}
            <div className="overflow-y-auto gray-scrollbar">
              <div className={`relative z-10 divide-y ${isDark ? 'divide-[#2E2F33]' : 'divide-gray-100'}`}>
                {sortedBookings.map(booking => {
                  const isPending = booking.status === 'pending';
                  const isCancelled = booking.status === 'cancelled';
                  const isActive = booking.status === 'active';
  
                return (
                  <div 
                    key={booking.id}
                    className={`grid grid-cols-[100px_1fr_180px_140px_110px_90px] gap-4 px-6 py-4 items-center transition-all ${
                      isDark ? 'hover:bg-[#2A3441]/30' : 'hover:bg-gray-50'
                    }`}
                  >
                    {/* Date Column */}
                    <div className="flex items-center">
                      <div className={`flex flex-col items-center justify-center w-11 h-12 rounded-lg border ${
                        isActive 
                          ? isDark ? 'bg-[#B59143]/10 border-[#B59143]/30 text-[#FBD282]' : 'bg-[#FFF8E6] border-[#B59143]/30 text-[#B59143]'
                          : isPending
                            ? isDark ? 'bg-orange-500/10 border-orange-500/30 text-orange-400' : 'bg-orange-50 border-orange-200 text-orange-500'
                            : isDark ? 'bg-gray-800/50 border-gray-700 text-gray-600' : 'bg-gray-100 border-gray-200 text-gray-400'
                      }`}>
                        <span className="text-[8px] font-bold uppercase tracking-wider leading-none mb-0.5 opacity-80">
                          {(() => {
                            const [year, month, day] = booking.date.split('-').map(Number);
                            const d = new Date(year, month - 1, day);
                            return d.toLocaleDateString('en-US', { month: 'short' });
                          })()}
                        </span>
                        <span className="text-base font-black leading-none">
                          {(() => {
                            const [year, month, day] = booking.date.split('-').map(Number);
                            const d = new Date(year, month - 1, day);
                            return d.getDate();
                          })()}
                        </span>
                        <span className="text-[7px] font-bold uppercase mt-0.5 opacity-60">
                          {(() => {
                            const [year, month, day] = booking.date.split('-').map(Number);
                            const d = new Date(year, month - 1, day);
                            return d.toLocaleDateString('en-US', { weekday: 'short' });
                          })()}
                        </span>
                      </div>
                    </div>
  
                    {/* Seat Column */}
                    <div>
                      <div className={`text-sm font-bold ${isActive || isPending ? (isDark ? 'text-white' : 'text-gray-900') : (isDark ? 'text-gray-500' : 'text-gray-400')}`}>
                        Seat {booking.seatName}
                      </div>
                    </div>
  
                    {/* Floor Column */}
                    <div>
                      <div className={`text-xs font-semibold ${isActive ? 'text-[#B59143]' : isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                        {booking.floorName || "Workspace"}
                      </div>
                    </div>
  
                    {/* Time Column */}
                    <div className="flex items-center gap-1.5">
                      <IconWrapper icon={FiClock} size={11} className={isDark ? 'text-gray-600' : 'text-gray-400'} />
                      <span className={`text-[11px] ${isDark ? 'text-gray-200' : 'text-gray-500'}`}>9:00 AM - 6:00 PM</span>
                    </div>
  
                    {/* Status Column - Premium Badge */}
                    <div>
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[9px] font-bold uppercase tracking-wider shadow-sm ${
                        isActive 
                          ? isDark 
                            ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 border border-green-500/30' 
                            : 'bg-gradient-to-r from-green-50 to-emerald-50 text-green-600 border border-green-200'
                          : isPending
                            ? isDark 
                              ? 'bg-gradient-to-r from-orange-500/20 to-amber-500/20 text-orange-400 border border-orange-500/30'
                              : 'bg-gradient-to-r from-orange-50 to-amber-50 text-orange-600 border border-orange-200'
                            : isDark 
                              ? 'bg-gray-800/50 text-gray-500 border border-gray-700'
                              : 'bg-gray-100 text-gray-500 border border-gray-200'
                      }`}>
                        {isActive && <IconWrapper icon={FiCheckCircle} size={10} />}
                        {isPending && <IconWrapper icon={FiClock} size={10} />}
                        {isCancelled && <IconWrapper icon={FiX} size={10} />}
                        {booking.status}
                      </div>
                    </div>
  
                    {/* Action Column - Premium Cancel Button */}
                    <div className="flex items-center justify-center">
                      {isActive && new Date(booking.date).setHours(0,0,0,0) >= new Date().setHours(0,0,0,0) ? (
                        <Tooltip label="Cancel Booking" hasArrow size="sm">
                          <button
                            onClick={() => onCancelBooking(booking.id)}
                            className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${
                              isDark 
                                ? 'bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 hover:border-red-500/50' 
                                : 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:border-red-300'
                            }`}
                          >
                            Cancel
                          </button>
                        </Tooltip>
                      ) : (
                        <div className="w-16" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};
